import {
  forwardRef, memo, useCallback, useContext, useEffect, useId, useMemo, useRef, useState,
  type FunctionComponent, type ReactNode
} from 'react';
import pick from 'lodash/pick';
import isNil from 'lodash/isNil';
import defaultTo from 'lodash/defaultTo';
import isEqual from 'lodash/isEqual';
import { useLazyQuery } from '@apollo/client';
import { useLocation } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
// Material UI imports
import { visuallyHidden } from '@mui/utils';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
// Skillmore UI Components
import { injectParams } from '@empathco/ui-components/src/helpers/path';
import { paramsDiffer } from '@empathco/ui-components/src/helpers/pagination';
import { pathBuilder } from '@empathco/ui-components/src/helpers/graphql';
import { POLLING_FETCH_POLICY } from '@empathco/ui-components/src/hooks/usePolling';
import useQueryCounted from '@empathco/ui-components/src/hooks/useQueryCounted';
import CloseIconButton from '@empathco/ui-components/src/elements/CloseIconButton';
import CardTitle from '@empathco/ui-components/src/elements/CardTitle';
import CardSection from '@empathco/ui-components/src/elements/CardSection';
import CardTabbar from '@empathco/ui-components/src/elements/CardTabbar';
import OnOffSwitch from '@empathco/ui-components/src/elements/OnOffSwitch';
// local imports
import { OPPORTUNITY_MATCHES_QUERY } from '../graphql/OpportunityMatches';
import { OPPORTUNITY_BOOKINGS_QUERY } from '../graphql/OpportunityBookings';
import {
  Opportunity, OpportunityMatch, OpportunityBookingsSort,
  OpportunityBookingsDocument, OpportunityBookingsQueryVariables,
  OpportunityMatchesDocument, OpportunityMatchesQueryVariables
} from '../graphql/types';
import { PATH_SUPERVISOR_OPPORTUNITY_BOOKINGS, PATH_SUPERVISOR_OPPORTUNITY_MATCHES } from '../config/paths';
import { getDefaultLeaderId } from '../models/user';
import { FilterValues } from '../hooks/useFilters';
import { GlobalContext } from '../context/global';
import { managerDashboardParams } from '../context/supervisor';
import { DataContext } from '../context';
import OpportunityMatchesCalculating from '../elements/OpportunityMatchesCalculating';
import DashboardFilters from '../v3/DashboardFilters';
import OpportunityMatchesPanel from '../panels/OpportunityMatchesPanel';
import OpportunityBookingPanel from '../panels/OpportunityBookingPanel';

type MatchesContentProps = {
  readonly loading: boolean;
  readonly count: number | null;
  readonly variables?: Partial<OpportunityMatchesQueryVariables>;
  readonly children: ReactNode;
};

const MatchesContent = forwardRef<HTMLDivElement, MatchesContentProps>(({ loading, count, variables, children }, ref) => {
  const { formatMessage } = useIntl();
  const [regionPainted, setRegionPainted] = useState(false);
  const [lastLoad, setLastLoad] = useState({ loading, variables });
  const [settledCount, setSettledCount] = useState<number | null>(null);
  if (lastLoad.loading !== loading || (!loading && !isEqual(lastLoad.variables, variables))) {
    setLastLoad({ loading, variables });
    setSettledCount(loading || !count ? null : count);
  } else if (isNil(count) && !isNil(settledCount)) setSettledCount(null);
  const settledValues = useMemo(() => ({ total: settledCount }), [settledCount]);
  useEffect(() => {
    let nextFrame = 0;
    const firstFrame = requestAnimationFrame(() => {
      nextFrame = requestAnimationFrame(() => setRegionPainted(true));
    });
    return () => {
      cancelAnimationFrame(firstFrame);
      cancelAnimationFrame(nextFrame);
    };
  }, []);
  const loadingMessage = loading ? <FormattedMessage id="opportunities.matches.loading"/> : undefined;
  const announcement = isNil(settledCount) ? loadingMessage
    : <FormattedMessage id="opportunities.matches.found" values={settledValues}/>;
  return (
    <Box ref={ref} tabIndex={-1} role="region" aria-label={formatMessage({ id: 'opportunities.matches.title' })}>
      <Box role="status" sx={visuallyHidden}>
        {regionPainted ? announcement : undefined}
      </Box>
      {children}
    </Box>
  );
});
MatchesContent.displayName = 'MatchesContent';

type OpportunityBookingsDialogProps = {
  readonly opportunity?: Opportunity | null;
  readonly onClose: () => void;
  readonly readOnly?: boolean;
  readonly matchesTimedOut?: boolean;
  readonly onMatchesRetry?: () => void;
}

const OpportunityBookingsDialog: FunctionComponent<OpportunityBookingsDialogProps> = ({
  opportunity,
  onClose,
  readOnly = false,
  matchesTimedOut = false,
  onMatchesRetry
  // eslint-disable-next-line max-statements
}) => {
  const { pathname } = useLocation();
  const titleId = useId();
  const { id, title, matches_pending } = opportunity || {};
  const calculating = Boolean(matches_pending);

  const {
    query: getMatches, pending: matchesPending, failed: matchesFailed, count: matchesCount, results: matches,
    variables: matchesVars
  } = useQueryCounted({
    data: undefined as unknown as OpportunityMatch,
    key: 'opportunityMatches',
    lazyQuery: useLazyQuery(OPPORTUNITY_MATCHES_QUERY as typeof OpportunityMatchesDocument)
  });

  const {
    query: getBookings, pending: bookingsPending, failed: bookingsFailed, count: bookingsCount, results: bookings,
    variables: bookingsVars
  } = useQueryCounted({
    data: undefined as unknown as OpportunityMatch,
    key: 'opportunityBookings',
    lazyQuery: useLazyQuery(OPPORTUNITY_BOOKINGS_QUERY as typeof OpportunityBookingsDocument)
  });

  const { user: { data: user } } = useContext(GlobalContext);
  const { settingsUpdate: { pending: pendingSettingsUpdate } } = useContext(DataContext);

  const [matchesPage, setMatchesPage] = useState(1);
  const [matchesPageSize, setMatchesPageSize] = useState<number>();
  const [hideShortlisted, setHideShortlisted] = useState(false);
  const [filters, setFilters] = useState<FilterValues>();

  const [bookingPage, setBookingPage] = useState(1);
  const [bookingPageSize, setBookingPageSize] = useState<number>();

  const uid = useMemo(() => getDefaultLeaderId(user), [user]);

  const managerDashboardParamsFunc = useMemo(managerDashboardParams, []);

  const [matchesLink, bookingsLink] = useMemo(() => [
    (id && injectParams(PATH_SUPERVISOR_OPPORTUNITY_MATCHES, { opp_id: id })) || undefined,
    (id && injectParams(PATH_SUPERVISOR_OPPORTUNITY_BOOKINGS, { opp_id: id })) || undefined
  ], [id]);

  const isMatchesTab = pathname === matchesLink;
  const isBookingsTab = pathname === bookingsLink;

  const freshMatches = useRef(true);
  useEffect(() => {
    if (!isMatchesTab || calculating) freshMatches.current = true;
  }, [isMatchesTab, calculating]);

  useEffect(() => {
    if (id && isMatchesTab && !calculating && matchesPageSize && filters && getMatches) {
      const variables: OpportunityMatchesQueryVariables = {
        opportunity_id: id,
        pathBuilder,
        input: {
          ...pick(managerDashboardParamsFunc(filters), ['manager_id', 'country_id', 'state_id', 'job_levels']),
          ...hideShortlisted ? { hide_shortlist: true } : {},
          limit: matchesPageSize
        }
      };
      let curPage = matchesPage;
      if (paramsDiffer(matchesVars?.input, variables?.input)) {
        curPage = 1;
        setMatchesPage(1);
      }
      variables.input.offset = matchesPageSize * (curPage - 1);
      getMatches({ variables, ...freshMatches.current ? POLLING_FETCH_POLICY : {} });
      freshMatches.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // ignoring `matchesVars` changes:
    id, isMatchesTab, calculating, hideShortlisted, filters, matchesPage, matchesPageSize,
    getMatches, managerDashboardParamsFunc
  ]);

  useEffect(() => {
    if (id && isBookingsTab && bookingPageSize && getBookings) {
      const variables: OpportunityBookingsQueryVariables = {
        opportunity_id: id,
        pathBuilder,
        input: {
          sort_by: OpportunityBookingsSort.match_rate,
          limit: bookingPageSize
        }
      };
      let curPage = bookingPage;
      if (paramsDiffer(bookingsVars?.input, variables?.input)) {
        curPage = 1;
        setBookingPage(1);
      }
      variables.input.offset = bookingPageSize * (curPage - 1);
      getBookings({ variables });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, bookingPage, bookingPageSize, isBookingsTab, getBookings]); // ignoring `bookingVars` changes

  const isOpen = isMatchesTab || isBookingsTab;

  const [mounted, setMounted] = useState(isOpen);
  useEffect(() => {
    if (isOpen) setMounted(true);
  }, [isOpen]);

  const transitionProps = useMemo(() => ({ onExited: () => {
    setMounted(false);
  } }), []);

  const handleFilters = useCallback((newFilters: FilterValues) => setFilters((prevFilters) => {
    if (isEqual(prevFilters, newFilters)) return prevFilters;
    return newFilters;
  }), []);

  const filtersReady = Boolean(filters);
  // const exportDisabled = !filtersReady;
  const dismissLocked = Boolean(pendingSettingsUpdate);
  const disabled = /* exportDisabled || */ dismissLocked ||
    (isMatchesTab && !calculating && matchesPending) || (isBookingsTab && bookingsPending) ? true : undefined;
  const filtersDisabled = disabled || calculating ? true : undefined;
  const matchesLoading = !calculating && (!filtersReady || matchesPending);

  const matchesContentRef = useRef<HTMLDivElement>(null);
  const handleMatchesRetry = useCallback(() => {
    matchesContentRef.current?.focus();
    onMatchesRetry?.();
  }, [onMatchesRetry]);

  const booking = (
    <OpportunityBookingPanel
        supervisor
        readOnly={readOnly}
        opportunity={opportunity}
        disabled={disabled}
        bookings={bookings}
        count={bookingsCount}
        pending={bookingsPending}
        failed={bookingsFailed}
        currentPage={bookingPage}
        onPageSelected={setBookingPage}
        onPageSize={setBookingPageSize}
    />
  );

  const items = useMemo(() => readOnly ? undefined : [
    {
      title: 'opportunities.matches.title',
      link: matchesLink
    },
    {
      title: 'opportunities.booking.title',
      link: bookingsLink
    }
  ], [matchesLink, bookingsLink, readOnly]);

  return mounted ? (
    <Dialog
        disableEnforceFocus
        maxWidth="xl"
        fullWidth
        scroll="body"
        open={isOpen}
        onClose={dismissLocked ? undefined : onClose}
        TransitionProps={transitionProps}
        aria-labelledby={titleId}
    >
      <CloseIconButton onClick={onClose} disabled={dismissLocked}/>
      <CardSection top>
        <Typography id={titleId} variant="subtitle2" fontStyle="italic" color="info.caption" component="span">
          {title}
        </Typography>
      </CardSection>
      {items ? (
        <CardTabbar withoutScroll items={items}>
          <>
            <CardSection top>
              <DashboardFilters
                  settingsId="opp_matches"
                  withLevels
                  withReset
                  onChange={handleFilters}
                  uid={uid}
                  disabled={filtersDisabled}
                  // action={ ... exportDisabled ...}
                  extraFilters={
                    <Box pl={2} pb={1} display="flex" justifyContent="flex-start">
                      <OnOffSwitch
                          label="opportunities.matches.hide_shortlist"
                          value={Boolean(hideShortlisted)}
                          onChange={setHideShortlisted}
                          disabled={filtersDisabled}
                      />
                    </Box>
                  }
              />
            </CardSection>
            <MatchesContent
                ref={matchesContentRef}
                loading={matchesLoading}
                count={calculating ? null : defaultTo(matchesCount, null)}
                variables={matchesVars}
            >
              {calculating ? (
                <OpportunityMatchesCalculating
                    variant="notice"
                    timedOut={matchesTimedOut}
                    onRetry={handleMatchesRetry}
                />
              ) : (
                <OpportunityMatchesPanel
                    opportunity={opportunity}
                    disabled={disabled}
                    matches={matches}
                    count={matchesCount}
                    pending={matchesLoading}
                    failed={matchesFailed}
                    currentPage={matchesPage}
                    onPageSelected={setMatchesPage}
                    onPageSize={setMatchesPageSize}
                />
              )}
            </MatchesContent>
          </>
          {booking}
        </CardTabbar>
      ) : (
        <>
          <CardTitle title="opportunities.booking.title" withDivider/>
          {booking}
        </>
      )}
    </Dialog>
  ) : null;
};

export default memo(OpportunityBookingsDialog);
